import { FC } from 'react';
import { AgentListItem } from '@/types/agents';
import AgentAvatar from './AgentAvatar';
import ScopeBadge from './ScopeBadge';

interface Props {
  agent: AgentListItem;
  onOpen: () => void;
  onConfigure?: () => void;
  onDelete?: () => void;
  // J05: fixar/desafixar no dashboard. Só renderiza o pin quando `onTogglePin` é passado.
  pinned?: boolean;
  onTogglePin?: () => void;
}

const AgentCard: FC<Props> = ({ agent, onOpen, onConfigure, onDelete, pinned, onTogglePin }) => {
  const owned = !!(onConfigure || onDelete);
  return (
    <div
      data-testid="agent-card"
      className="text-left rounded-2xl bg-surface-secondary border border-surface-border hover:border-accent/60 hover:shadow-lg transition p-5 group flex flex-col relative"
    >
      {onTogglePin && (
        <button
          type="button"
          data-testid="pin-toggle"
          aria-label={pinned ? `Desafixar ${agent.name}` : `Fixar ${agent.name}`}
          aria-pressed={pinned}
          title={pinned ? 'Desafixar do dashboard' : 'Fixar no dashboard'}
          onClick={onTogglePin}
          className={`absolute top-3 right-3 grid place-items-center w-8 h-8 rounded-lg transition ${
            pinned ? 'text-accent' : 'text-content-tertiary hover:text-accent'
          } hover:bg-surface-tertiary`}
        >
          <span
            className="material-symbols-outlined text-lg"
            style={pinned ? { fontVariationSettings: "'FILL' 1" } : undefined}
          >
            push_pin
          </span>
        </button>
      )}
      <button type="button" onClick={onOpen} className="text-left flex items-start gap-4 flex-1">
        <AgentAvatar avatar={agent.avatar} />
        <div className="flex-1 min-w-0 pr-8">
          <h3 className="font-semibold text-content-primary truncate">{agent.name}</h3>
          <div className="mt-1 flex items-center gap-2">
            <ScopeBadge scope={agent.scope} />
            <span className="text-xs px-2 py-0.5 rounded-full bg-surface-tertiary text-content-tertiary">
              {agent.type === 'SQL' ? 'SQL' : 'RAG'}
            </span>
          </div>
          <p className="mt-3 text-sm text-content-secondary line-clamp-3">{agent.description}</p>
        </div>
      </button>

      <div className="mt-4 flex items-center justify-between text-xs text-content-tertiary">
        <span>{agent.total_messages} mensagens</span>
        {owned ? (
          <div className="flex items-center gap-3">
            {onConfigure && (
              <button
                type="button"
                data-testid="agent-configure"
                onClick={onConfigure}
                className="text-accent hover:underline"
              >
                Configurar
              </button>
            )}
            {onDelete && (
              <button
                type="button"
                data-testid="agent-delete"
                onClick={onDelete}
                className="text-red-400 hover:underline"
              >
                Excluir
              </button>
            )}
          </div>
        ) : (
          <button type="button" onClick={onOpen} className="text-accent opacity-0 group-hover:opacity-100 transition">
            Abrir →
          </button>
        )}
      </div>
    </div>
  );
};

export default AgentCard;
